import { ArrowsRightLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { Application } from '../../types'
import { getValidTransitions, STATUS_LABELS, isTerminalStatus } from '../../utils/statusMachine'

type Props = {
  app: Application | null
  statusDraft: Application['status']
  offerReasonDraft: string
  onStatusDraftChange: (status: Application['status']) => void
  onOfferReasonDraftChange: (reason: string) => void
  onClose: () => void
  onSave: () => void
}

export const StatusChangeModal = ({
  app,
  statusDraft,
  offerReasonDraft,
  onStatusDraftChange,
  onOfferReasonDraftChange,
  onClose,
  onSave,
}: Props) => {
  if (!app) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-app-ink flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-4 w-4 text-sky-700 dark:text-app-accent" aria-hidden="true" />
            Change status: {app.company}
          </p>
          <button
            onClick={onClose}
            className="text-app-muted hover:text-app-ink"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          {isTerminalStatus(app.status) ? (
            <div className="rounded-lg border border-app-line bg-app-subtle px-3 py-3 text-sm text-app-muted">
              <span className="font-medium text-app-ink">{STATUS_LABELS[app.status]}</span>
              <span className="ml-2 text-xs">(final status - cannot be changed)</span>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-xs uppercase text-app-muted font-medium">New status</label>
                <select
                  value={statusDraft}
                  onChange={(e) => onStatusDraftChange(e.target.value as Application['status'])}
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                >
                  <option value={app.status}>{STATUS_LABELS[app.status]} (current)</option>
                  {getValidTransitions(app.status).map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>

              {(statusDraft === 'offer_declined' || statusDraft === 'accepted') && (
                <div className="space-y-1">
                  <label className="text-xs uppercase text-app-muted font-medium">Decision reason</label>
                  <input
                    type="text"
                    value={offerReasonDraft}
                    onChange={(e) => onOfferReasonDraftChange(e.target.value)}
                    placeholder="e.g. better offer, budget constraints..."
                    className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle"
          >
            Cancel
          </button>
          {!isTerminalStatus(app.status) && (
            <button
              onClick={onSave}
              disabled={statusDraft === app.status}
              className="rounded-lg bg-app-primary px-4 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

